import React, { useState, useEffect } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import { TrendingUp, Loader, ChevronDown } from 'lucide-react';

export default function KpiTestCreatedCount() {
  const [allPlans, setAllPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [planCounts, setPlanCounts] = useState({});
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);

  // Charger tous les plans au montage
  useEffect(() => {
    const loadPlans = async () => {
      try {
        console.log('📋 Chargement des plans de test...');
        const plans = await azureDevOpsService.getAllTestPlans();
        setAllPlans(plans);
        console.log(`✓ ${plans.length} plan(s) chargé(s)`);
      } catch (err) {
        console.error('❌ Erreur lors du chargement des plans:', err);
        setAllPlans([]);
      } finally {
        setLoadingPlans(false);
      }
    };

    loadPlans();
  }, []);

  // Calculer le nombre de test cases pour un plan
  const calculatePlanCount = async (planId) => {
    setLoadingPlanId(planId);
    try {
      console.log(`🔍 Comptage test cases pour plan ${planId}...`);
      const count = await azureDevOpsService.getTestCaseCountByPlanRecursive(planId);
      setPlanCounts(prev => ({
        ...prev,
        [planId]: count
      }));
      console.log(`✓ Plan ${planId}: ${count} test case(s)`);
    } catch (err) {
      console.error(`❌ Erreur pour plan ${planId}:`, err);
      setPlanCounts(prev => ({
        ...prev,
        [planId]: -1 // Indicateur d'erreur
      }));
    } finally {
      setLoadingPlanId(null);
    }
  };

  // Sélectionner un plan depuis la dropdown
  const selectPlan = (planId) => {
    if (!selectedPlanIds.includes(planId)) {
      setSelectedPlanIds(prev => [...prev, planId]);
      calculatePlanCount(planId);
    }
    setShowDropdown(false);
  };

  // Désélectionner un plan
  const deselectPlan = (planId) => {
    setSelectedPlanIds(prev => prev.filter(id => id !== planId));
    setPlanCounts(prev => {
      const updated = { ...prev };
      delete updated[planId];
      return updated;
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-blue-500">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Test Cases par Plan</h2>
          <p className="text-gray-600 text-sm mt-1">Sélectionnez un plan pour compter ses test cases</p>
        </div>
        <TrendingUp className="w-10 h-10 text-blue-500" />
      </div>

      {/* Dropdown Section */}
      <div className="mb-8 bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="relative w-full md:w-96">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={loadingPlans}
            className="w-full px-4 py-3 bg-white border border-blue-300 rounded-lg text-left font-medium text-gray-700 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 flex items-center justify-between"
          >
            <span>
              {loadingPlans ? '📋 Chargement des plans...' : `📋 Sélectionner un plan (${allPlans.length})`}
            </span>
            <ChevronDown 
              className={`w-5 h-5 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Dropdown List */}
          {showDropdown && !loadingPlans && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-blue-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
              {allPlans.length > 0 ? (
                allPlans.map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => selectPlan(plan.id)}
                    disabled={selectedPlanIds.includes(plan.id)}
                    className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b last:border-b-0 disabled:bg-blue-100 disabled:opacity-50 transition-colors flex justify-between items-center"
                  >
                    <div>
                      <p className="font-medium text-gray-800 line-clamp-1">{plan.name}</p>
                      <p className="text-xs text-gray-500">ID: {plan.id}</p>
                    </div>
                    {selectedPlanIds.includes(plan.id) && (
                      <span className="text-green-600 font-bold">✓</span>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-gray-600 text-sm">Aucun plan disponible</div>
              )}
            </div>
          )}
        </div>

        {selectedPlanIds.length > 0 && (
          <p className="mt-3 text-sm text-blue-700">
            <strong>{selectedPlanIds.length}</strong> plan(s) sélectionné(s)
          </p>
        )}
      </div>

      {/* Selected Plans Results */}
      {selectedPlanIds.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Résultats</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedPlanIds.map(planId => {
              const plan = allPlans.find(p => p.id === planId);
              const count = planCounts[planId];
              const isLoading = loadingPlanId === planId;

              return (
                <div
                  key={planId}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-5 border border-gray-300 shadow-sm hover:shadow-md transition-shadow relative"
                >
                  {/* Close Button */}
                  <button
                    onClick={() => deselectPlan(planId)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-lg font-bold"
                  >
                    ✕
                  </button>

                  {/* Plan Info */}
                  <h4 className="font-semibold text-gray-800 mb-2 pr-6 line-clamp-2">
                    {plan?.name || `Plan ${planId}`}
                  </h4>
                  <p className="text-xs text-gray-600 mb-4">
                    <span className="font-mono bg-gray-200 px-2 py-1 rounded text-blue-700">
                      ID: {planId}
                    </span>
                  </p>

                  {/* Count or Loading */}
                  {isLoading ? (
                    <div className="mb-4">
                      <div className="flex items-center gap-2">
                        <Loader className="w-4 h-4 animate-spin text-blue-600" />
                        <p className="text-sm text-gray-600">Calcul en cours...</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        ⏳ Le calcul peut prendre quelques secondes...
                      </p>
                    </div>
                  ) : count !== undefined ? (
                    <div className="mb-4">
                      {count === -1 ? (
                        <p className="text-lg font-bold text-red-600">❌ Erreur</p>
                      ) : (
                        <div>
                          <p className="text-4xl font-bold text-green-700">{count}</p>
                          <p className="text-xs text-gray-600 mt-1">test case(s)</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mb-4">
                      <p className="text-sm text-gray-500">En attente...</p>
                    </div>
                  )}

                  {/* Recalculate Button */}
                  {!isLoading && count !== undefined && (
                    <button
                      onClick={() => calculatePlanCount(planId)}
                      className="w-full px-3 py-2 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors"
                    >
                      🔄 Recalculer
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {selectedPlanIds.length === 0 && !loadingPlans && (
        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-600">
          <p className="text-sm">👆 Sélectionnez un plan de test dans la dropdown ci-dessus pour voir le nombre de test cases</p>
        </div>
      )}
    </div>
  );
}
